import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plane, Hotel, Car, Hand, Columns2, LayoutList, Music, Users } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import UploadDataModal from '../components/UploadDataModal'
import DashboardAssetsModal from '../components/DashboardAssetsModal'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import {
  getDataAPI,
  getEffectiveUserId,
  getActiveArtistIds,
  getArtistFromCache,
  postDataAPI,
  setActiveArtistIds,
  setArtistCache,
} from '../lib/api'
import { loadLinks } from '../lib/linksStore'
import { buildTicketPublicUrl } from '../lib/ticketUrls'
import { buildGuestListPublicUrl } from '../lib/guestListUrls'
import { dizeeInboxAddress } from '../lib/dizeeInboxAddress'
import { useUserPermissions } from '../hooks/useUserPermissions'
import { normalizeList } from '../lib/normalize'
import { makeCacheKey, readSessionCache, writeSessionCache } from '../lib/sessionCache'
import { computeDashboardHeroCardModel } from '../lib/overviewHeroCardModel'
import DashboardHeroCard from '../components/DashboardHeroCard'
import { Skeleton } from '../components/ui/Skeleton'
import {
  computeIntelligence,
  parseBonusThreshold,
  type IntelligenceSignal,
  type ShowBonusTier,
  type ShowData,
  type SongData,
  type FinanceData,
  type TaskData,
} from '../lib/intelligenceEngine'
import {
  normalizeDashboardTasksFromPayload,
  getTaskAssigneeIds,
  taskNeedsApproval,
  formatDuePhrase,
  taskDueInNextWeekOrOverdue,
  isTaskCompleted,
  type DashboardTask,
} from '../lib/taskDashboardUtils'
import { parseLocalDate, toLocalDateKey, shortDateDisplay } from '../lib/dateUtils'
import { findTourShowIdForBooking, tmListFromApiPayload, tmTravelListFromApi } from '../lib/tourShowBookingLink'
import { summarizeMoneyPeriod, listFinancesInPeriod, type MoneyRangeId } from '../lib/dashboardMoney'
import {
  extractArray,
  normalizeBookings,
  normalizeCatalog,
  normalizeFinancials,
  deriveShowFinanceEntries,
  deriveDealFinanceEntries,
  mergeUniqueFinances,
  isUpcomingSong,
} from '../lib/dashboardNormalize'
import { catalogArtworkDisplayUrl } from '../lib/artworkUrl'
import { lodgingTypeLabel } from '../lib/touringApi'
import { groundPickupLocationLabel, groundTransportCardHeading } from '../lib/tourGroundTransportDisplay'
import { GlassCard, SlideTag, StatRow } from './wrapped/WrappedSlides'

/** Matches digest show-detail ticket bar low-sell highlight. */
const BRAND = '#FF2300'

function ShowStatusBadge({ status, label }: { status: 'red' | 'yellow' | 'green' | 'blue'; label: string }) {
  const colors = {
    red: 'bg-red-500/20 text-red-400',
    yellow: 'bg-amber-500/20 text-amber-400',
    green: 'bg-emerald-500/20 text-emerald-400',
    blue: 'bg-sky-500/20 text-sky-400',
  }
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${colors[status]}`}>{label}</span>
  )
}

function parseShowSellableCap(show: ShowData | undefined): number {
  if (!show) return 0
  const raw = show.sellable_cap ?? show.capacity ?? 0
  return Number(String(raw).replace(/[^0-9]/g, '')) || 0
}

function getShowSetTime(show: ShowData | undefined): string {
  if (!show) return ''
  const top = typeof show.show_time === 'string' ? show.show_time.trim() : ''
  if (top) return top
  try {
    const raw = show.raw as Record<string, unknown> | undefined
    const st = raw?.show_time
    if (typeof st === 'string' && st.trim()) return st.trim()
    if (st != null && st !== '') return String(st).trim()
  } catch {
    /* ignore */
  }
  return ''
}

function bonusTiersFromShow(show: ShowData | undefined): ShowBonusTier[] {
  if (!show) return []
  const raw = show.raw as Record<string, unknown> | undefined
  const t = show.bonus_tiers ?? raw?.bonus_tiers
  if (!Array.isArray(t)) return []
  const out: ShowBonusTier[] = []
  for (const x of t) {
    if (!x || typeof x !== 'object') continue
    const o = x as { tickets_threshold?: unknown; bonus_amount?: unknown }
    const tickets_threshold = Number(o.tickets_threshold) || 0
    const bonus_amount = Number(o.bonus_amount) || 0
    if (tickets_threshold > 0 && bonus_amount > 0) out.push({ tickets_threshold, bonus_amount })
  }
  return out.sort((a, b) => a.tickets_threshold - b.tickets_threshold)
}

/** Next milestone or “all earned” for structured bonus tiers. */
function activeBonusMilestone(
  tiers: ShowBonusTier[],
  sold: number,
): { amount: number; threshold: number; allEarned: boolean } | null {
  if (!tiers.length) return null
  const sorted = [...tiers].sort((a, b) => a.tickets_threshold - b.tickets_threshold)
  const last = sorted[sorted.length - 1]
  if (sold >= last.tickets_threshold) {
    return { amount: last.bonus_amount, threshold: last.tickets_threshold, allEarned: true }
  }
  for (const t of sorted) {
    if (sold < t.tickets_threshold) {
      return { amount: t.bonus_amount, threshold: t.tickets_threshold, allEarned: false }
    }
  }
  return { amount: last.bonus_amount, threshold: last.tickets_threshold, allEarned: true }
}

/** Guarantee, bonus, and set time — matches Shows detail fields; respects show-level financial visibility. */
function DashboardShowTermsCard({
  show,
  hideFinancials,
}: {
  show: ShowData | undefined
  hideFinancials: boolean
}) {
  const guarantee = show?.guarantee ? Number(String(show.guarantee).replace(/[^0-9.]/g, '')) : 0
  const setTimeStr = getShowSetTime(show)
  const bonusSold = Number(show?.tix_sold ?? show?.ticket_count ?? 0) || 0
  const stringParsed = show?.bonus ? parseBonusThreshold(show.bonus) : null
  const tiers = bonusTiersFromShow(show)
  const milestone = tiers.length ? activeBonusMilestone(tiers, bonusSold) : null

  const bonusAmount = stringParsed?.amount ?? milestone?.amount ?? 0
  const bonusThreshold = stringParsed?.threshold ?? milestone?.threshold ?? 1
  const bonusUnlocked = stringParsed
    ? bonusSold >= stringParsed.threshold
    : milestone
      ? milestone.allEarned
      : false
  const showBonusProgress = Boolean(stringParsed || milestone)
  const bonusProgress =
    bonusThreshold > 0 ? Math.min(100, Math.round((bonusSold / bonusThreshold) * 100)) : 0
  const multiTierNote =
    !stringParsed && tiers.length > 1 ? `${tiers.length} tiers · next milestone` : null

  return (
    <GlassCard className="p-3">
      {!hideFinancials && (
        <StatRow
          label="Guarantee"
          value={guarantee > 0 ? `$${guarantee.toLocaleString()}` : '—'}
          accent={guarantee > 0}
        />
      )}
      {!hideFinancials && showBonusProgress && (
        <div className="border-b border-gray-200 py-2.5 dark:border-white/[0.04]">
          <div className="mb-2 flex items-center justify-between">
            <span className={DASH_LABEL}>Bonus</span>
            {bonusUnlocked ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/20">
                Earned
              </span>
            ) : (
              <span className="text-[10px] font-semibold text-amber-400">{bonusProgress}%</span>
            )}
          </div>
          <div className="mb-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/[0.06]">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${bonusUnlocked ? 100 : Math.max(2, bonusProgress)}%`,
                backgroundColor: bonusUnlocked ? '#22c55e' : '#f59e0b',
              }}
            />
          </div>
          <p className="text-[11px] text-gray-600 dark:text-zinc-500">
            {bonusUnlocked && !stringParsed && tiers.length > 1
              ? `All ${tiers.length} bonus tiers earned · $${tiers.reduce((s, t) => s + t.bonus_amount, 0).toLocaleString()} potential`
              : `${bonusSold.toLocaleString()} / ${bonusThreshold.toLocaleString()} toward $${bonusAmount.toLocaleString()} bonus`}
          </p>
          {multiTierNote && !bonusUnlocked ? (
            <p className="mt-1 text-[10px] text-gray-500 dark:text-zinc-600">{multiTierNote}</p>
          ) : null}
        </div>
      )}
      {!hideFinancials && !showBonusProgress && (
        <StatRow
          label="Bonus"
          value={
            tiers.length
              ? tiers.map((t) => `$${t.bonus_amount.toLocaleString()} @ ${t.tickets_threshold.toLocaleString()} tix`).join(' · ')
              : show?.bonus?.trim() || '—'
          }
        />
      )}
      <StatRow label="Set time" value={setTimeStr || '—'} />
    </GlassCard>
  )
}

/** Matches Shows list / overview — omit canceled shows from dashboard pacing tiles. */
function isShowCanceled(show: ShowData | undefined): boolean {
  if (!show) return false
  const s = (show.status || '').toLowerCase().trim()
  if (s === 'canceled' || s === 'cancelled') return true
  try {
    const raw = show.raw as Record<string, unknown> | undefined
    const st = raw && typeof raw.status === 'string' ? raw.status.toLowerCase().trim() : ''
    return st === 'canceled' || st === 'cancelled'
  } catch {
    return false
  }
}

/** Persisted so refresh keeps Intelligence dashboard artist scope (single id or "all"). */
const INTEL_SCOPE_STORAGE_KEY = 'dizee:intelDashboardScope'
const ALL_ARTISTS_VALUE = '__all__'

/** Warm the digest chunk so the Digest action rarely waits on network. */
function prefetchDigestPage() {
  void import('./DigestPage')
}

function mergeRawPayloadArrays(payloads: unknown[]): unknown[] {
  const seen = new Set<string>()
  const out: unknown[] = []
  for (const payload of payloads) {
    for (const item of extractArray(payload)) {
      const row = item as { _id?: string; id?: string }
      const id = String(row._id ?? row.id ?? '')
      if (id) {
        if (seen.has(id)) continue
        seen.add(id)
      }
      out.push(item)
    }
  }
  return out
}

function mergeDealsPayloads(parts: unknown[]): unknown {
  const deals: unknown[] = []
  const seen = new Set<string>()
  for (const payload of parts) {
    const raw = (payload as { data?: { deals?: unknown[] }; deals?: unknown[] })?.data ?? payload
    const list = Array.isArray((raw as { deals?: unknown[] })?.deals)
      ? (raw as { deals: unknown[] }).deals
      : Array.isArray(raw)
        ? raw
        : []
    for (const deal of list) {
      const rec = deal as { _id?: string; id?: string }
      const id = String(rec._id ?? rec.id ?? '')
      if (id) {
        if (seen.has(id)) continue
        seen.add(id)
      }
      deals.push(deal)
    }
  }
  return { data: { deals } }
}

function mergeCalendarPayloads(parts: (unknown | null)[]): { data: { data: { events: unknown[] } } } {
  const events: unknown[] = []
  const seen = new Set<string>()
  for (const raw of parts) {
    if (!raw) continue
    const p = raw as { data?: { data?: { events?: unknown[] }; events?: unknown[] }; events?: unknown[] }
    const list =
      p?.data?.data?.events ??
      (p?.data as { events?: unknown[] })?.events ??
      p?.events ??
      []
    const arr = Array.isArray(list) ? list : []
    for (const e of arr) {
      const rec = e as { id?: string; iCalUID?: string }
      const id = String(rec.id ?? rec.iCalUID ?? '')
      const key = id || `e-${events.length}`
      if (seen.has(key)) continue
      seen.add(key)
      events.push(e)
    }
  }
  return { data: { data: { events } } }
}

function clearSessionStorageExceptIntelScope() {
  try {
    const scope = sessionStorage.getItem(INTEL_SCOPE_STORAGE_KEY)
    sessionStorage.clear()
    if (scope) sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, scope)
  } catch {
    sessionStorage.clear()
  }
}

/** Single scroll region per SectionCard — children should not nest another overflow-y-auto (breaks wheel/touch scrolling). */
const MODULE_BODY_SCROLL =
  'flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-y-contain intel-scroll'

const MODULE_BODY_INNER = 'min-w-0 -mx-1 px-1'

/** Digest-style primary title (matches Shows pacing rows). */
const DASH_H2 = 'text-xl font-semibold tracking-tight text-gray-900 dark:text-white'

const DASH_META = 'text-[11px] text-gray-600 dark:text-zinc-500'
const DASH_SUB = 'text-[12px] text-gray-600 dark:text-zinc-400'
const DASH_LABEL = 'text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500'
const DASH_VALUE = 'text-[13px] font-semibold text-gray-900 dark:text-white'

const DASHBOARD_MODULE_LAYOUT_KEY = 'dizee:dashboardModuleLayout'
const DASHBOARD_MODULE_ORDER_KEY = 'dizee:dashboardModuleOrder'

type DashboardModuleId = 'shows' | 'music' | 'tasks' | 'money'
type DashboardModuleLayout = 'sideBySide' | 'stacked'

const ALL_MODULE_IDS: DashboardModuleId[] = ['shows', 'music', 'tasks', 'money']

const MODULE_TITLES: Record<DashboardModuleId, string> = {
  shows: 'Shows',
  music: 'Music',
  tasks: 'Tasks',
  money: 'Money',
}

function normalizeDashboardModuleOrder(raw: unknown): DashboardModuleId[] {
  const valid = new Set<DashboardModuleId>(ALL_MODULE_IDS)
  const seen = new Set<DashboardModuleId>()
  const out: DashboardModuleId[] = []
  if (Array.isArray(raw)) {
    for (const x of raw) {
      const id = x as DashboardModuleId
      if (valid.has(id) && !seen.has(id)) {
        seen.add(id)
        out.push(id)
      }
    }
  }
  for (const id of ALL_MODULE_IDS) {
    if (!seen.has(id)) out.push(id)
  }
  return out
}

/** Layout toggle + drag reorder only on desktop (Tailwind `lg` / 1024px). */
const DASHBOARD_MODULE_DESKTOP_MQ = '(min-width: 1024px)'

function useDashboardModuleDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(DASHBOARD_MODULE_DESKTOP_MQ).matches
  })
  useEffect(() => {
    const mq = window.matchMedia(DASHBOARD_MODULE_DESKTOP_MQ)
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    setIsDesktop(mq.matches)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isDesktop
}

function songMetadataPills(song: SongData | undefined): { label: string; value: string }[] {
  if (!song) return []
  const raw = (song.raw || {}) as Record<string, unknown>
  const isrc = raw.isrc ?? raw.ISRC
  const genre = raw.genre ?? raw.Genre
  const duration = raw.duration ?? raw.Duration
  const label = raw.label ?? raw.Label
  const pills: { label: string; value: string }[] = []
  if (isrc) pills.push({ label: 'ISRC', value: String(isrc) })
  if (genre) pills.push({ label: 'Genre', value: String(genre) })
  if (duration != null && duration !== '') pills.push({ label: 'Duration', value: String(duration) })
  if (label) pills.push({ label: 'Label', value: String(label) })
  return pills
}

type HeroRawBundle = {
  shows: unknown
  songs: unknown
  tasks: unknown
  calendar: unknown
}

/**
 * Dashboard module shell — light surfaces in light mode; digest (#13120A) in dark mode.
 * No orange sliver — that stays on digest-only slides.
 */
function SectionCard({
  title,
  children,
  headerEnd,
}: {
  title: string
  children: React.ReactNode
  /** Top-right slot (e.g. drag handle). */
  headerEnd?: React.ReactNode
}) {
  return (
    <div className="w-full min-w-0">
      <div
        className="relative flex w-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/[0.08] dark:bg-[#13120A] dark:shadow-none"
        style={{ height: 580 }}
      >
        <div className="flex h-full min-h-0 flex-col px-6 pb-8 pt-6 text-gray-900 dark:text-white">
          <div className="flex shrink-0 items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 flex-1 pt-0.5">
              <SlideTag>{title}</SlideTag>
            </div>
            {headerEnd}
          </div>
          <div className={`mt-3 ${MODULE_BODY_SCROLL}`}>{children}</div>
        </div>
      </div>
    </div>
  )
}

function SortableModuleSection({
  id,
  title,
  children,
}: {
  id: DashboardModuleId
  title: string
  children: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.92 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} className="w-full min-w-0">
      <SectionCard
        title={title}
        headerEnd={
          <button
            type="button"
            className="group shrink-0 -mr-1 -mt-1 cursor-grab touch-none rounded-xl p-2 transition-colors hover:bg-gray-100/90 active:cursor-grabbing dark:hover:bg-white/[0.06]"
            aria-label="Drag to reorder module"
            {...attributes}
            {...listeners}
          >
            <Hand
              className="pointer-events-none h-4 w-4 text-gray-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100 dark:text-zinc-400"
              aria-hidden
              strokeWidth={1.75}
            />
          </button>
        }
      >
        {children}
      </SectionCard>
    </div>
  )
}

type BookingTravelBundle = {
  tourShowId: string | null
  flights: unknown[]
  hotels: unknown[]
  ground: unknown[]
  crew: unknown[]
}

function travelRowTourShowId(row: unknown): string {
  if (!row || typeof row !== 'object') return ''
  const r = row as { tourShowId?: unknown }
  const v = r.tourShowId
  if (v == null) return ''
  if (typeof v === 'object' && v !== null && '_id' in v) {
    const id = (v as { _id?: unknown })._id
    return id == null ? '' : String(id)
  }
  return String(v)
}

function travelItemsForTourShow(items: unknown[], tourShowId: string): unknown[] {
  if (!tourShowId) return []
  return items.filter((x) => travelRowTourShowId(x) === tourShowId)
}

function tmRowDocId(row: unknown): string {
  if (!row || typeof row !== 'object') return ''
  const r = row as { _id?: unknown; id?: unknown }
  const id = r._id ?? r.id
  if (id != null && typeof id === 'object' && 'toString' in id) {
    return String((id as { toString: () => string }).toString())
  }
  return typeof id === 'string' ? id : id != null ? String(id) : ''
}

function formatDashShortDate(raw: unknown): string {
  if (raw == null || raw === '') return ''
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? '' : raw.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  const d = new Date(raw as string | number)
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function dashFlightLegCity(f: Record<string, unknown>, leg: 'departure' | 'arrival'): string {
  const o = f[leg]
  if (o && typeof o === 'object' && !Array.isArray(o)) {
    const city = (o as { city?: unknown }).city
    const airport = (o as { airport?: unknown }).airport
    if (typeof city === 'string' && city.trim()) return city.trim()
    if (typeof airport === 'string' && airport.trim()) return airport.trim()
  }
  const flat = leg === 'departure' ? f.departureCity : f.arrivalCity
  if (typeof flat === 'string' && flat.trim()) return flat.trim()
  return ''
}

function dashFlightLegTime(f: Record<string, unknown>, leg: 'departure' | 'arrival'): string {
  const o = f[leg]
  const nested =
    o && typeof o === 'object' && !Array.isArray(o) ? (o as { time?: unknown }).time : undefined
  const flat = leg === 'departure' ? f.departureTime : f.arrivalTime
  const raw = nested ?? flat
  if (raw == null || raw === '') return ''
  if (raw instanceof Date) return raw.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  const d = new Date(raw as string | number)
  if (Number.isNaN(d.getTime())) {
    return typeof raw === 'string' || typeof raw === 'number' ? String(raw) : ''
  }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function formatDashFlightSummary(f: unknown, index: number): string {
  if (!f || typeof f !== 'object') return `Flight ${index + 1}`
  const fl = f as Record<string, unknown>
  const num = String(fl.flightNumber ?? fl.flight_number ?? '').trim()
  const dep = dashFlightLegCity(fl, 'departure')
  const arr = dashFlightLegCity(fl, 'arrival')
  const depT = dashFlightLegTime(fl, 'departure')
  const route = dep && arr ? `${dep} → ${arr}` : dep || arr || 'Route TBD'
  const head = num || `Flight ${index + 1}`
  const status = String(fl.status ?? '').trim()
  const statusBit = status && status !== 'option' ? ` · ${status}` : ''
  return depT ? `${head} · ${route} · ${depT}${statusBit}` : `${head} · ${route}${statusBit}`
}

function formatDashLodgingSummary(h: unknown): string {
  if (!h || typeof h !== 'object') return 'Lodging'
  const r = h as Record<string, unknown>
  const name = String(r.hotelName ?? r.name ?? 'Lodging').trim() || 'Lodging'
  const ltRaw = r.lodgingType ?? r.lodging_type
  const typeLbl = lodgingTypeLabel(typeof ltRaw === 'string' ? ltRaw : undefined)
  const addr = r.address as Record<string, unknown> | undefined
  const city = String(r.city ?? addr?.city ?? '').trim()
  const st = String(r.state ?? addr?.state ?? '').trim()
  const locality = city && st ? `${city}, ${st}` : city || st
  const ci = r.checkIn ?? r.check_in ?? r.check_in_date
  const co = r.checkOut ?? r.check_out ?? r.check_out_date
  const ciStr = formatDashShortDate(ci)
  const coStr = formatDashShortDate(co)
  const stay = ciStr && coStr ? `${ciStr} – ${coStr}` : ciStr || coStr
  const status = String(r.status ?? '').trim()
  const statusBit = status && status !== 'option' ? ` · ${status}` : ''
  const bits = [name, typeLbl && typeLbl !== 'Lodging' ? typeLbl : null, locality, stay ? `Stay: ${stay}` : null]
    .filter(Boolean)
    .join(' · ')
  return `${bits}${statusBit}`
}

/** Dashboard ground line: "Leaving at …" or type/fallback; with pickup location → "Leaving at … from …". */
function formatDashGroundSummary(g: unknown, index: number): string {
  if (!g || typeof g !== 'object') return `Ground ${index + 1}`
  const row = g as Record<string, unknown>
  const { heading, usedPickupTimeInHeading } = groundTransportCardHeading(
    row as { transportType?: unknown; transport_type?: unknown; type?: unknown; pickup?: unknown },
    index,
  )
  if (!usedPickupTimeInHeading) return heading
  const loc = groundPickupLocationLabel(row.pickup)
  return loc ? `${heading} from ${loc}` : heading
}

function formatDashCrewSummary(c: unknown): string {
  if (!c || typeof c !== 'object') return 'Crew'
  const r = c as Record<string, unknown>
  const name = String(r.name ?? '').trim()
  const role = String(r.role ?? '').trim()
  if (name && role) return `${name} · ${role}`
  return name || role || 'Crew'
}

function DashboardShowTravelBlocks({ bundle }: { bundle: BookingTravelBundle }) {
  const noTour = !bundle.tourShowId
  const emptyLeg = noTour ? '—' : 'Nothing added yet.'

  const rowClass =
    'text-[11px] text-gray-700 dark:text-zinc-300 leading-snug border-l-2 border-violet-500/35 pl-2.5 py-0.5'

  return (
    <div className="space-y-2">
      {noTour ? (
        <p className="text-[10px] text-amber-700/90 dark:text-amber-400/90 leading-relaxed">
          No tour stop linked to this booking yet — open the show on Shows to sync travel.
        </p>
      ) : null}

      <GlassCard className="p-3">
        <div className="mb-2 flex items-center gap-2">
          <Plane className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
          <span className={DASH_LABEL}>Flights</span>
        </div>
        {bundle.flights.length === 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-zinc-500">{emptyLeg}</p>
        ) : (
          <ul className="space-y-2">
            {bundle.flights.map((f, i) => (
              <li key={tmRowDocId(f) || `f-${i}`} className={rowClass}>
                {formatDashFlightSummary(f, i)}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      <GlassCard className="p-3">
        <div className="mb-2 flex items-center gap-2">
          <Hotel className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
          <span className={DASH_LABEL}>Lodging</span>
        </div>
        {bundle.hotels.length === 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-zinc-500">{emptyLeg}</p>
        ) : (
          <ul className="space-y-2">
            {bundle.hotels.map((h, i) => (
              <li key={tmRowDocId(h) || `h-${i}`} className={rowClass}>
                {formatDashLodgingSummary(h)}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      <GlassCard className="p-3">
        <div className="mb-2 flex items-center gap-2">
          <Car className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
          <span className={DASH_LABEL}>Ground</span>
        </div>
        {bundle.ground.length === 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-zinc-500">{emptyLeg}</p>
        ) : (
          <ul className="space-y-2">
            {bundle.ground.map((g, i) => (
              <li key={tmRowDocId(g) || `g-${i}`} className={rowClass}>
                {formatDashGroundSummary(g, i)}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      <GlassCard className="p-3">
        <div className="mb-2 flex items-center gap-2">
          <Users className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
          <span className={DASH_LABEL}>Crew</span>
        </div>
        {bundle.crew.length === 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-zinc-500">{emptyLeg}</p>
        ) : (
          <ul className="space-y-2">
            {bundle.crew.map((c, i) => (
              <li key={tmRowDocId(c) || `c-${i}`} className={rowClass}>
                {formatDashCrewSummary(c)}
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
    </div>
  )
}

function DashboardShowsCombined({
  pacingSignals,
  riskSignals,
  showsById,
  hideFinancials = false,
  travelDetailByBookingId,
}: {
  pacingSignals: IntelligenceSignal[]
  riskSignals: IntelligenceSignal[]
  showsById: Map<string, ShowData>
  hideFinancials?: boolean
  travelDetailByBookingId: Record<string, BookingTravelBundle>
}) {
  const atRiskShowIds = useMemo(() => {
    const s = new Set<string>()
    for (const r of riskSignals) {
      if (r.source === 'shows' && r.relatedEntityId) s.add(String(r.relatedEntityId))
    }
    return s
  }, [riskSignals])

  const visiblePacingSignals = useMemo(
    () =>
      pacingSignals.filter((sig) => {
        const id = sig.relatedEntityId ? String(sig.relatedEntityId) : ''
        const show = id ? showsById.get(id) : undefined
        return !isShowCanceled(show)
      }),
    [pacingSignals, showsById],
  )

  /**
   * Tiles that are not in pacing: (1) ticket tracking off, (2) tracking on but no sellable cap.
   * Pacing signals only exist when cap &gt; 0, so upcoming “next shows” like the hero would still omit (2) without this.
   */
  const secondaryShowRows = useMemo(() => {
    const inPacing = new Set(
      visiblePacingSignals.map((s) => (s.relatedEntityId ? String(s.relatedEntityId) : '')).filter(Boolean),
    )
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const isUpcoming = (show: ShowData) => {
      const d = parseLocalDate(show.date || show.perf_date || '')
      return Boolean(d && d >= today)
    }

    const trackingOff = Array.from(showsById.values())
      .filter((show) => {
        const showId = String(show.id)
        if (isShowCanceled(show)) return false
        if (inPacing.has(showId)) return false
        if (show.ticket_tracking !== false) return false
        return isUpcoming(show)
      })
      .sort(
        (a, b) =>
          (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
          (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
      )
      .slice(0, 8)

    const toRow = (show: ShowData, variant: 'trackingOff' | 'needsCapacity') => {
      const showDate = parseLocalDate(show.date || show.perf_date || '')
      const label = show.title || show.event_title || show.venue || 'Show'
      const city = show.city ? ` — ${show.city}` : ''
      const daysUntil = showDate
        ? Math.max(0, Math.ceil((showDate.getTime() - today.getTime()) / 86_400_000))
        : null
      return {
        id: String(show.id),
        title: `${label}${city}`,
        daysUntil,
        variant,
      }
    }

    const trackingOffRows = trackingOff.map((s) => toRow(s, 'trackingOff'))
    const trackingOffIds = new Set(trackingOffRows.map((r) => r.id))

    const needsCapacity = Array.from(showsById.values())
      .filter((show) => {
        const showId = String(show.id)
        if (isShowCanceled(show)) return false
        if (inPacing.has(showId)) return false
        if (trackingOffIds.has(showId)) return false
        if (show.ticket_tracking === false) return false
        if (parseShowSellableCap(show) > 0) return false
        return isUpcoming(show)
      })
      .sort(
        (a, b) =>
          (parseLocalDate(a.date || a.perf_date || '')?.getTime() ?? 0) -
          (parseLocalDate(b.date || b.perf_date || '')?.getTime() ?? 0),
      )
      .slice(0, 8)

    const needsCapacityRows = needsCapacity.map((s) => toRow(s, 'needsCapacity'))

    return [...trackingOffRows, ...needsCapacityRows]
  }, [visiblePacingSignals, showsById])

  const mergedShowRows = useMemo(() => {
    const pacingPart = visiblePacingSignals.map((signal) => {
      const showId = signal.relatedEntityId ? String(signal.relatedEntityId) : ''
      const show = showId ? showsById.get(showId) : undefined
      const d = show ? parseLocalDate(show.date || show.perf_date || '') : null
      const sortTime = d?.getTime() ?? Number.MAX_SAFE_INTEGER
      return { kind: 'pacing' as const, signal, sortTime }
    })
    const ntPart = secondaryShowRows.map((row) => {
      const show = showsById.get(row.id)
      const d = show ? parseLocalDate(show.date || show.perf_date || '') : null
      const sortTime = d?.getTime() ?? Number.MAX_SAFE_INTEGER
      return { kind: 'notTracking' as const, row, sortTime }
    })
    return [...pacingPart, ...ntPart].sort((a, b) => a.sortTime - b.sortTime)
  }, [visiblePacingSignals, secondaryShowRows, showsById])

  if (mergedShowRows.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
        No upcoming shows in this view yet. Add a sellable capacity on a booking for ticket pacing, or turn ticket
        tracking off to list it here without pacing.
      </p>
    )
  }

  return (
    <div className={MODULE_BODY_INNER}>
      <div className="space-y-5">
        {mergedShowRows.map((item) => {
          if (item.kind === 'pacing') {
            const { signal } = item
            const showId = signal.relatedEntityId ? String(signal.relatedEntityId) : ''
            const atRisk = showId && atRiskShowIds.has(showId)
            const show = showId ? showsById.get(showId) : undefined
            const setTimeStr = getShowSetTime(show)

            const primaryTitle = show?.event_title || show?.title || show?.venue || signal.title
            const sold = Number(show?.tix_sold ?? show?.ticket_count ?? 0) || 0
            const cap = parseShowSellableCap(show)
            const ticketPct = cap > 0 ? Math.round((sold / cap) * 100) : 0
            const daysUntil = signal.meta?.daysUntil ?? '?'
            const isPositive = (signal.delta ?? 0) >= 0
            const dateStr = show ? shortDateDisplay(show.date || show.perf_date || '') : ''

            const showHref = showId ? `/shows?focus=${encodeURIComponent(showId)}` : signal.link || '/shows'
            const travelBundle: BookingTravelBundle = showId
              ? travelDetailByBookingId[showId] ?? {
                  tourShowId: null,
                  flights: [],
                  hotels: [],
                  ground: [],
                  crew: [],
                }
              : { tourShowId: null, flights: [], hotels: [], ground: [], crew: [] }

            return (
              <Link key={signal.id} to={showHref} className="block">
              <h2 className={DASH_H2}>{primaryTitle}</h2>
              <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${DASH_META}`}>
                {show?.city && (
                  <span>
                    {show.city}
                    {show.state ? `, ${show.state}` : ''}
                  </span>
                )}
                {dateStr && <span>{dateStr}</span>}
                {setTimeStr && <span>{setTimeStr}</span>}
                {cap > 0 && <span>Cap: {cap.toLocaleString()}</span>}
                {atRisk && <ShowStatusBadge status="red" label="At risk" />}
                <span className="text-gray-600 dark:text-zinc-600">{daysUntil}d out</span>
              </div>
              {signal.comparison && (
                <p className={`mt-2 text-[12px] ${isPositive ? 'text-emerald-400' : 'text-amber-400'}`}>{signal.comparison}</p>
              )}

              <div className="mt-3 space-y-2">
                {cap > 0 && (
                  <GlassCard className="p-3">
                    <div className="mb-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                        Ticket sales
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/[0.06]">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, Math.max(2, ticketPct))}%`,
                          backgroundColor: ticketPct >= 70 ? '#22c55e' : ticketPct >= 40 ? '#f59e0b' : BRAND,
                        }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] text-gray-600 dark:text-zinc-600">{ticketPct}% sold</p>
                  </GlassCard>
                )}

                <DashboardShowTermsCard show={show} hideFinancials={hideFinancials} />

                <DashboardShowTravelBlocks bundle={travelBundle} />
              </div>
              </Link>
            )
          }

          const { row } = item
          const variant = row.variant ?? 'trackingOff'
          const show = showsById.get(row.id)
          const dateStr = show ? shortDateDisplay(show.date || show.perf_date || '') : ''
          const setTimeStr = getShowSetTime(show)
          const sellableCap = parseShowSellableCap(show)

          const travelBundleSec: BookingTravelBundle = travelDetailByBookingId[row.id] ?? {
            tourShowId: null,
            flights: [],
            hotels: [],
            ground: [],
            crew: [],
          }

          return (
            <Link key={`secondary-${variant}-${row.id}`} to={`/shows?focus=${encodeURIComponent(row.id)}`} className="block">
              <div className="flex items-start justify-between gap-2">
                <h2 className={DASH_H2}>{row.title}</h2>
                {variant === 'trackingOff' ? (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-600 bg-gray-100 dark:bg-white/[0.08] dark:text-zinc-400">
                    Tracking off
                  </span>
                ) : (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700 bg-sky-100 dark:bg-sky-500/15 dark:text-sky-300">
                    Needs sellable cap
                  </span>
                )}
              </div>
              <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 ${DASH_META}`}>
                {dateStr && <span>{dateStr}</span>}
                {setTimeStr && <span>{setTimeStr}</span>}
                {row.daysUntil != null && <span>{row.daysUntil}d out</span>}
              </div>

              <div className="mt-3 space-y-2">
                {variant === 'needsCapacity' ? (
                  <GlassCard className="p-3">
                    <p className={`text-[11px] leading-relaxed ${DASH_SUB}`}>
                      Set a sellable capacity on this booking to see ticket pacing and % sold here.
                    </p>
                  </GlassCard>
                ) : null}
                <DashboardShowTermsCard show={show} hideFinancials={hideFinancials} />
                {sellableCap > 0 && (
                  <GlassCard className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                        Capacity
                      </span>
                      <span className={`${DASH_VALUE} tabular-nums`}>{sellableCap.toLocaleString()}</span>
                    </div>
                  </GlassCard>
                )}

                <DashboardShowTravelBlocks bundle={travelBundleSec} />
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function DashboardMusicRowCover({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    setFailed(false)
  }, [src])
  if (failed) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Music className="h-5 w-5 text-gray-400 dark:text-zinc-600" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={name}
      className="h-full w-full object-cover"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  )
}

function DashboardMusicCombined({
  catalogSongs,
  streamingSignals,
}: {
  catalogSongs: SongData[]
  streamingSignals: IntelligenceSignal[]
}) {
  const songById = useMemo(() => new Map(catalogSongs.map((s) => [String(s.id), s])), [catalogSongs])

  const rows = useMemo(() => {
    const ids = new Set<string>()
    for (const sig of streamingSignals) {
      if (sig.relatedEntityId) ids.add(String(sig.relatedEntityId))
    }
    for (const s of catalogSongs) {
      const id = String(s.id)
      if (isUpcomingSong(s)) ids.add(id)
    }
    const list = Array.from(ids)
      .map((id) => {
        const song = songById.get(id)
        const streamSig = streamingSignals.find((x) => String(x.relatedEntityId) === id)
        return { id, song, streamSig }
      })
      .filter((r) => r.song || r.streamSig)

    list.sort((a, b) => {
      const da = a.song?.release_date ? parseLocalDate(a.song.release_date)?.getTime() ?? 0 : 0
      const db = b.song?.release_date ? parseLocalDate(b.song.release_date)?.getTime() ?? 0 : 0
      if (da !== db) return db - da
      const sa = Number(a.streamSig?.meta?.streamsTotal ?? a.song?.streams_total ?? 0) || 0
      const sb = Number(b.streamSig?.meta?.streamsTotal ?? b.song?.streams_total ?? 0) || 0
      return sb - sa
    })
    return list
  }, [catalogSongs, streamingSignals])

  if (rows.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
        No music activity in this view yet.
      </p>
    )
  }

  return (
    <div className={MODULE_BODY_INNER}>
      <div className="space-y-5">
        {rows.map(({ id, song, streamSig }) => {
          const name = streamSig?.title || song?.song_title || song?.title || 'Track'
          const streamsToday = streamSig?.meta?.streamsToday ?? song?.streams_today ?? 0
          const streamsTotal = streamSig?.meta?.streamsTotal ?? song?.streams_total ?? 0
          const upcoming = song ? isUpcomingSong(song) : false
          const pills = songMetadataPills(song)
          const artworkUrl = song ? catalogArtworkDisplayUrl(song) : null

          return (
            <Link key={id} to={`/music?focus=${encodeURIComponent(id)}`} className="block">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-white/[0.06]">
                  {artworkUrl ? (
                    <DashboardMusicRowCover src={artworkUrl} name={name} />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Music className="h-5 w-5 text-gray-400 dark:text-zinc-600" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className={DASH_H2}>{name}</h2>
                  {song?.artist_name && <p className={`mt-0.5 text-[12px] ${DASH_SUB}`}>{song.artist_name}</p>}
                  {song?.release_date && <p className={`mt-0.5 text-[11px] ${DASH_META}`}>{shortDateDisplay(song.release_date)}</p>}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {upcoming && (
                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700 bg-sky-100 dark:bg-sky-500/20 dark:text-sky-300">
                    Upcoming
                  </span>
                )}
              </div>
              {pills.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pills.map((p) => (
                    <span
                      key={`${id}-${p.label}`}
                      className="rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-medium text-gray-600 dark:bg-white/[0.06] dark:text-zinc-400"
                    >
                      {p.label}: {p.value}
                    </span>
                  ))}
                </div>
              )}
              {streamSig?.comparison && <p className={`mt-2 leading-relaxed ${DASH_SUB}`}>{streamSig.comparison}</p>}
              <div className="mt-3 space-y-2">
                <GlassCard className="p-3 text-center">
                  <p className="text-lg font-semibold tabular-nums tracking-tight leading-none text-gray-900 dark:text-white">
                    {streamsTotal.toLocaleString()}
                  </p>
                  <p className={`mt-1.5 text-[11px] font-medium ${DASH_META}`}>Total streams</p>
                </GlassCard>
                {streamsToday > 0 && (
                  <GlassCard className="p-3 text-center">
                    <p className="text-[15px] font-semibold tabular-nums text-gray-900 dark:text-white">
                      {streamsToday.toLocaleString()}
                    </p>
                    <p className={`mt-1 text-[10px] font-medium ${DASH_META}`}>Streams today</p>
                  </GlassCard>
                )}
                <GlassCard className="p-3">
                  <p className={`mb-1.5 ${DASH_LABEL}`}>Platform breakdown</p>
                  <p className={`text-[12px] leading-relaxed ${DASH_SUB}`}>
                    Per-platform stats available when connected.
                  </p>
                </GlassCard>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

function DashboardTasksWeek({
  tasks,
  memberMap,
}: {
  tasks: DashboardTask[]
  memberMap: Record<string, string>
}) {
  const [openTask, setOpenTask] = useState<DashboardTask | null>(null)

  const weekTasks = useMemo(() => {
    return tasks
      .filter((t) => !isTaskCompleted(t) && t.due_date && taskDueInNextWeekOrOverdue(t.due_date))
      .sort((a, b) => {
        const da = toLocalDateKey(parseLocalDate(a.due_date) || new Date(0))
        const db = toLocalDateKey(parseLocalDate(b.due_date) || new Date(0))
        return da.localeCompare(db)
      })
  }, [tasks])

  const resolveName = useCallback(
    (uid: string) => {
      const n = memberMap[uid]
      if (n) return n
      return uid.length > 8 ? `${uid.slice(0, 6)}…` : uid
    },
    [memberMap],
  )

  if (weekTasks.length === 0) {
    return (
      <p className="py-1 text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
        No overdue tasks and nothing due in the next week.
      </p>
    )
  }

  return (
    <>
      <div className={MODULE_BODY_INNER}>
        <div className="space-y-5">
          {weekTasks.map((t) => {
            const ids = getTaskAssigneeIds(t)
            const first = ids[0]
            const rest = Math.max(0, ids.length - 1)
            const approval = taskNeedsApproval(t)
            const duePhrase = formatDuePhrase(t.due_date)
            const taskRowId = String((t as { _id?: string })._id || t.id || '')

            return (
              <div key={t.id} className="block">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <Link
                    to={taskRowId ? `/tasks?focus=${encodeURIComponent(taskRowId)}` : '/tasks'}
                    className="min-w-0 flex-1 rounded-2xl px-2.5 py-2.5 transition-[background-color,box-shadow] duration-200 ease-out hover:bg-gray-100/65 hover:shadow-[0_1px_0_rgba(0,0,0,0.03)] dark:hover:bg-white/[0.045] dark:hover:shadow-[0_1px_0_rgba(255,255,255,0.04)]"
                  >
                    <h2 className={DASH_H2}>{t.title}</h2>
                    <p className={`mt-1 text-[11px] ${DASH_META}`}>{duePhrase}</p>
                  </Link>
                </div>
                <GlassCard className="mt-3 p-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {approval && (
                      <span className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 dark:bg-violet-500/20 dark:text-violet-300">
                        Approval needed
                      </span>
                    )}
                    {ids.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setOpenTask(t)}
                        className="min-w-0 text-left text-[11px] text-gray-600 underline-offset-2 transition-colors hover:text-gray-900 hover:underline dark:text-zinc-400 dark:hover:text-white"
                      >
                        Assigned to {first ? resolveName(first) : '—'}
                        {rest > 0 ? ` +${rest}` : ''}
                      </button>
                    )}
                  </div>
                </GlassCard>
              </div>
            )
          })}
        </div>
      </div>
      {openTask && (
        <Modal isOpen={!!openTask} onClose={() => setOpenTask(null)} title="Assigned to" maxWidthClassName="max-w-sm" mobileCentered>
          <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-2">
            {getTaskAssigneeIds(openTask).map((uid) => (
              <li key={uid}>{resolveName(uid)}</li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  )
}

function DashboardMoneySection({
  finances,
  hideFinancials,
}: {
  finances: FinanceData[]
  /** When true (team member without overview financial access), show shell only — no amounts or activity. */
  hideFinancials: boolean
}) {
  const [range, setRange] = useState<MoneyRangeId>('30d')
  const currentBalance = useMemo(() => {
    try {
      const uid = getEffectiveUserId() || 'default'
      const raw = localStorage.getItem(`dizee:money:currentBalance:${uid}`)
      if (raw == null || raw === '') return 0
      const n = Number(raw)
      return Number.isFinite(n) ? n : 0
    } catch {
      return 0
    }
  }, [])
  const summary = useMemo(() => summarizeMoneyPeriod(finances, range, currentBalance), [finances, range, currentBalance])

  const periodTx = useMemo(() => listFinancesInPeriod(finances, range), [finances, range])
  const txCount = periodTx.length

  const fmt = (n: number) => {
    const safe = Number.isFinite(n) ? n : 0
    return safe.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const filters: { id: MoneyRangeId; label: string }[] = [
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '6m', label: '6 months' },
  ]

  if (hideFinancials) {
    return (
      <div className="flex min-h-0 min-w-0 flex-col">
        <GlassCard className="p-3">
          <p className="text-[13px] leading-relaxed text-gray-700 dark:text-zinc-300">
            Financial details aren&apos;t shared with your role for this artist profile.
          </p>
          <p className="mt-2 text-[12px] leading-relaxed text-gray-600 dark:text-zinc-500">
            Balances and transactions stay hidden until the artist enables financial access for you in Team permissions. You can still use the rest of the dashboard.
          </p>
        </GlassCard>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setRange(f.id)}
            className={`rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors ${
              range === f.id
                ? 'bg-gray-900 text-white dark:bg-white/[0.08] dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-zinc-600 dark:hover:text-zinc-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <GlassCard className="mb-3 p-3">
        <StatRow label="Starting balance" value={fmt(summary.startingBalance)} />
        <div className="flex items-baseline justify-between border-b border-gray-200 py-2.5 dark:border-white/[0.04]">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Money in</span>
          <span className="text-[15px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{fmt(summary.moneyIn)}</span>
        </div>
        <div className="flex items-baseline justify-between border-b border-gray-200 py-2.5 dark:border-white/[0.04]">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Money out</span>
          <span className="text-[15px] font-semibold tabular-nums text-red-600 dark:text-red-400">{fmt(summary.moneyOut)}</span>
        </div>
        <div className="flex items-baseline justify-between pt-2.5">
          <span className="text-[13px] text-gray-500 dark:text-zinc-500">Net</span>
          <span
            className={`text-[15px] font-semibold tabular-nums ${
              summary.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {summary.net >= 0 ? '+' : ''}
            {fmt(summary.net)}
          </span>
        </div>
      </GlassCard>

      <p className={`mb-2 shrink-0 ${DASH_LABEL}`}>Activity</p>
      <div className={MODULE_BODY_INNER}>
        {txCount === 0 ? (
          <p className="py-1 text-[13px] text-gray-500 dark:text-zinc-500">No transactions in this period.</p>
        ) : (
          <GlassCard className="p-3">
            <ul className="space-y-2.5">
              {periodTx.map((f, idx) => {
                const isRev = f.type === 'revenue'
                const amt = Number(f.amount) || 0
                return (
                  <li
                    key={`${f.id}-${idx}`}
                    className="flex items-start justify-between gap-2 border-b border-gray-200 pb-3 text-[12px] last:border-0 last:pb-0 dark:border-white/[0.04]"
                  >
                    <div className="min-w-0">
                      <span className="text-gray-500 dark:text-zinc-500">{shortDateDisplay(f.date)}</span>
                      <span className="mx-1 text-gray-400 dark:text-zinc-600">·</span>
                      <span className="break-words text-gray-800 dark:text-zinc-300">
                        {f.description || f.source || (isRev ? 'Revenue' : 'Spend')}
                      </span>
                    </div>
                    <span
                      className={`shrink-0 tabular-nums font-semibold ${
                        isRev ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {isRev ? '+' : '−'}
                      {fmt(amt)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </GlassCard>
        )}
      </div>
    </div>
  )
}

type ManagedArtistRow = {
  _id: string
  name: string
  email?: string
  role: string
  accountType?: 'artist' | 'promoter'
}

export default function DashboardOverviewPage() {
  const navigate = useNavigate()
  const [artistScopeTick, setArtistScopeTick] = useState(0)
  useEffect(() => {
    const onScope = () => setArtistScopeTick((t) => t + 1)
    window.addEventListener('dizee:activeArtistsChanged', onScope)
    return () => window.removeEventListener('dizee:activeArtistsChanged', onScope)
  }, [])

  const userId = getEffectiveUserId()
  const { isFinancialHidden } = useUserPermissions()
  /** Show-level money (guarantee, bonus tracking in pacing) — uses Shows granular permission. */
  const hideFinancialsShows = isFinancialHidden('shows')
  /** Intelligence dashboard Money card — uses Overview granular permission. */
  const hideFinancialsOverview = isFinancialHidden('overview')

  const [user] = useState<Record<string, unknown> | null>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? (JSON.parse(raw) as Record<string, unknown>) : null
    } catch {
      return null
    }
  })
  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  const isTeamMember = Boolean(user?.professionRole && typeof user.professionRole === 'string' && ['manager', 'support', 'promoter'].includes(user.professionRole))

  const shouldWaitForManagedArtists = Boolean(user?.email && token && isTeamMember)

  const [managedArtists, setManagedArtists] = useState<ManagedArtistRow[]>([])
  const [managedArtistsHydrated, setManagedArtistsHydrated] = useState(() => !shouldWaitForManagedArtists)

  useEffect(() => {
    if (!user?.email || !token) {
      setManagedArtistsHydrated(true)
      return
    }
    if (!isTeamMember) {
      setManagedArtistsHydrated(true)
      return
    }
    let cancelled = false
    postDataAPI('team/managed-artists', { teamMemberEmail: user.email }, token)
      .then((res) => {
        if (cancelled) return
        const raw = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(raw) ? raw : []
        setManagedArtists(list)
        setArtistCache(list)
      })
      .catch(() => {
        if (!cancelled) setManagedArtists([])
      })
      .finally(() => {
        if (!cancelled) setManagedArtistsHydrated(true)
      })
    return () => {
      cancelled = true
    }
  }, [user?.email, token, isTeamMember])
  /** Same list as Team → managed-artists (backend). Shown when you can act for more than one account. */
  const showArtistFilter = isTeamMember && managedArtists.length > 1

  const [intelScope, setIntelScope] = useState<'all' | string>(() => {
    try {
      const s = sessionStorage.getItem(INTEL_SCOPE_STORAGE_KEY)
      if (s === ALL_ARTISTS_VALUE) return 'all'
      if (s && s.length >= 8) return s
    } catch {
      /* ignore */
    }
    return ''
  })

  const resolvedDashboardArtistId =
    userId && managedArtists.some((a) => a._id === userId) ? userId : managedArtists[0]?._id ?? ''

  /** Cache + fetch key: combined view vs single-artist (primary or chosen id). */
  const scopeCacheKey = useMemo(() => {
    if (intelScope === 'all') {
      if (managedArtists.length === 0) return 'all:pending'
      return `all:${managedArtists.map((a) => a._id).sort().join(',')}`
    }
    if (intelScope && intelScope !== 'all') return intelScope
    return userId ?? ''
  }, [intelScope, managedArtists, userId])

  const handleDashboardArtistChange = (artistId: string) => {
    try {
      sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, artistId)
    } catch {
      /* ignore */
    }
    const current = getActiveArtistIds()
    const others = current.filter((id) => id !== artistId)
    setActiveArtistIds([artistId, ...others])
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        (key.startsWith('showsPageCache') ||
          key.startsWith('musicPageCatalogCache') ||
          key.startsWith('showsTicketTrackingOverrides') ||
          key.startsWith('pendingBandsintownShows'))
      ) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k))
    clearSessionStorageExceptIntelScope()
    window.dispatchEvent(new CustomEvent('dizee:activeArtistsChanged'))
  }

  const handleIntelArtistFilterChange = (value: string) => {
    if (value === ALL_ARTISTS_VALUE) {
      try {
        sessionStorage.setItem(INTEL_SCOPE_STORAGE_KEY, ALL_ARTISTS_VALUE)
      } catch {
        /* ignore */
      }
      setIntelScope('all')
      clearSessionStorageExceptIntelScope()
      return
    }
    setIntelScope(value)
    handleDashboardArtistChange(value)
  }

  const artistSelectValue =
    intelScope === 'all'
      ? ALL_ARTISTS_VALUE
      : intelScope || resolvedDashboardArtistId || managedArtists[0]?._id || ''

  const cachedSignals = readSessionCache<IntelligenceSignal[]>(makeCacheKey([scopeCacheKey, 'intelligence', 'signals']))
  const cachedAt = readSessionCache<number>(makeCacheKey([scopeCacheKey, 'intelligence', 'cachedAt']))
  const hasFreshCache = cachedSignals && cachedAt && Date.now() - cachedAt < 5 * 60 * 1000

  const [signals, setSignals] = useState<IntelligenceSignal[]>(cachedSignals || [])
  const [isLoading, setIsLoading] = useState(!hasFreshCache)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(cachedAt ? new Date(cachedAt) : null)
  const [finances, setFinances] = useState<FinanceData[]>([])
  const [dashboardTasks, setDashboardTasks] = useState<DashboardTask[]>([])
  const [memberMap, setMemberMap] = useState<Record<string, string>>({})
  const [tmFlights, setTmFlights] = useState<unknown[]>([])
  const [tmHotels, setTmHotels] = useState<unknown[]>([])
  const [tmGroundTransport, setTmGroundTransport] = useState<unknown[]>([])
  const [tmCrew, setTmCrew] = useState<unknown[]>([])
  /** Tour-manager show rows tagged with `_dashboardArtistId` for booking ↔ tour matching in multi-account views. */
  const [tmShowsIndex, setTmShowsIndex] = useState<Record<string, unknown>[]>([])

  const [heroRaw, setHeroRaw] = useState<HeroRawBundle | null>(() => {
    try {
      const shows = readSessionCache(makeCacheKey([userId, 'api', 'user/getBookings']))
      const songs = readSessionCache(makeCacheKey([userId, 'api', 'user/getCatalog']))
      const tasks = readSessionCache(makeCacheKey([userId, 'api', 'user/tasks']))
      const calendar =
        readSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=250'])) ??
        readSessionCache(makeCacheKey([userId, 'api', 'user/connectors/googleCalendar/events?maxResults=50']))
      if (shows || songs || tasks || calendar) {
        return {
          shows: shows ?? [],
          songs: songs ?? [],
          tasks: tasks ?? [],
          calendar: calendar ?? { data: { data: { events: [] } } },
        }
      }
    } catch {
      /* ignore */
    }
    return null
  })

  const applyData = (
    showsData: unknown,
    songsData: unknown,
    financialsData: unknown,
    tasksData: unknown,
    dealsData: unknown,
    cacheKey: string,
  ) => {
    const shows = normalizeBookings(showsData)
    const songs = normalizeCatalog(songsData)
    const finBase = normalizeFinancials(financialsData)
    const finDerived = [
      ...deriveShowFinanceEntries(showsData),
      ...deriveDealFinanceEntries(dealsData),
    ]
    const fin = mergeUniqueFinances(finBase, finDerived)
    const tasks = normalizeList<TaskData>(extractArray(tasksData) as TaskData[])

    setFinances(fin)
    setDashboardTasks(normalizeDashboardTasksFromPayload(tasksData))

    const computed = computeIntelligence({
      shows,
      songs,
      finances: fin,
      tasks,
    })
    setSignals(computed)
    setLastUpdated(new Date())
    writeSessionCache(makeCacheKey([cacheKey, 'intelligence', 'signals']), computed)
    writeSessionCache(makeCacheKey([cacheKey, 'intelligence', 'cachedAt']), Date.now())
  }

  /** Team: keep loading state true until managed-artists resolves (avoids empty dashboard flash when cache was keyed wrong). */
  useEffect(() => {
    if (isTeamMember && !managedArtistsHydrated) {
      setIsLoading(true)
    }
  }, [isTeamMember, managedArtistsHydrated])

  /** When scope key updates after managed-artists loads, re-hydrate signals from session cache (initial state used `all:pending`). */
  useEffect(() => {
    if (isTeamMember && !managedArtistsHydrated) return
    const cached = readSessionCache<IntelligenceSignal[]>(makeCacheKey([scopeCacheKey, 'intelligence', 'signals']))
    const at = readSessionCache<number>(makeCacheKey([scopeCacheKey, 'intelligence', 'cachedAt']))
    if (cached && at && Date.now() - at < 5 * 60 * 1000) {
      setSignals(cached)
      setLastUpdated(new Date(at))
      setIsLoading(false)
    }
  }, [scopeCacheKey, managedArtistsHydrated, isTeamMember])

  useEffect(() => {
    /** Team accounts: wait for managed-artists before fetching. Otherwise we hit the single-artist path with
     *  empty `managedArtists`, then re-fetch when the list loads — two skeleton / loading phases. */
    if (isTeamMember && !managedArtistsHydrated) {
      return
    }

    const fetchData = async () => {
      const effectiveUserId = getEffectiveUserId()
      try {
        if (intelScope === 'all' && managedArtists.length > 0) {
          const artistIds = managedArtists.map((a) => a._id)
          const bundles = await Promise.all(
            artistIds.map(async (aid) => {
              const params = { params: { userId: aid } }
              const [shows, songs, financials, tasks, dealsData, cal250, cal50] = await Promise.all([
                getDataAPI('user/getBookings', undefined, params).catch(() => []),
                getDataAPI('user/getCatalog', undefined, params).catch(() => []),
                getDataAPI('financials', undefined, params).catch(() => []),
                getDataAPI('user/tasks?scope=all', undefined, params).catch(() =>
                  getDataAPI('user/tasks', undefined, params).catch(() => []),
                ),
                (async () => {
                  try {
                    return await getDataAPI('deals/revenue-entries', undefined, params)
                  } catch {
                    try {
                      return await getDataAPI('deals', undefined, params)
                    } catch {
                      return { data: { deals: [] } }
                    }
                  }
                })(),
                getDataAPI('user/connectors/googleCalendar/events?maxResults=250', undefined, params).catch(() => null),
                getDataAPI('user/connectors/googleCalendar/events?maxResults=50', undefined, params).catch(() => null),
              ])
              return { shows, songs, financials, tasks, dealsData, cal250, cal50 }
            }),
          )

          const shows = mergeRawPayloadArrays(
            bundles.map((b, i) =>
              extractArray(b.shows).map((item: Record<string, unknown>) => ({
                ...item,
                _dashboardArtistId: artistIds[i],
              })),
            ),
          )
          const songs = mergeRawPayloadArrays(bundles.map((b) => b.songs))
          const financials = mergeRawPayloadArrays(bundles.map((b) => b.financials))
          const tasks = mergeRawPayloadArrays(bundles.map((b) => b.tasks))
          const dealsData = mergeDealsPayloads(bundles.map((b) => b.dealsData))
          const calendarData = mergeCalendarPayloads(bundles.map((b) => b.cal250 ?? b.cal50))

          setHeroRaw({ shows, songs, tasks, calendar: calendarData })
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getBookings']), shows)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getCatalog']), songs)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'financials']), financials)
          writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/tasks']), tasks)
          writeSessionCache(
            makeCacheKey([scopeCacheKey, 'api', 'user/connectors/googleCalendar/events?maxResults=250']),
            calendarData,
          )
          applyData(shows, songs, financials, tasks, dealsData, scopeCacheKey)

          const map: Record<string, string> = {}
          for (const aid of artistIds) {
            try {
              const response = await postDataAPI('team/members', { userId: aid, userRole: 'artist' })
              const data = response?.data?.data ?? response?.data ?? response
              const arr = Array.isArray(data) ? data : []
              for (const member of arr) {
                if (member.status !== 'active') continue
                const id = String(member._id ?? member.id)
                map[id] = member.name ?? member.username ?? member.email ?? id
              }
            } catch {
              /* ignore */
            }
          }
          try {
            const raw = localStorage.getItem('dizeeUser')
            const u = raw ? JSON.parse(raw) : null
            if (u?._id) map[String(u._id)] = u.name ?? u.username ?? map[String(u._id)]
          } catch {
            /* ignore */
          }
          setMemberMap(map)
          try {
            let allTmShows: Record<string, unknown>[] = []
            let allFlights: unknown[] = []
            let allHotels: unknown[] = []
            let allGround: unknown[] = []
            let allCrew: unknown[] = []
            for (const aid of artistIds) {
              const params = { params: { userId: aid } }
              const [showRes, tf, th, tg, tc] = await Promise.all([
                getDataAPI('user/tm/shows', undefined, { params: { userId: aid, limit: '5000' } }).catch(() => []),
                getDataAPI('user/tm/flights', undefined, params).catch(() => []),
                getDataAPI('user/tm/hotels', undefined, params).catch(() => []),
                getDataAPI('user/tm/ground-transport', undefined, params).catch(() => []),
                getDataAPI('user/tm/crew', undefined, params).catch(() => []),
              ])
              const tShows = tmListFromApiPayload(showRes, 'shows') as Record<string, unknown>[]
              for (const s of tShows) {
                allTmShows.push({ ...s, _dashboardArtistId: aid })
              }
              allFlights.push(...tmTravelListFromApi(tf, 'flights'))
              allHotels.push(...tmTravelListFromApi(th, 'hotels'))
              allGround.push(...tmTravelListFromApi(tg, 'ground'))
              allCrew.push(...tmTravelListFromApi(tc, 'crew'))
            }
            setTmShowsIndex(allTmShows)
            setTmFlights(allFlights)
            setTmHotels(allHotels)
            setTmGroundTransport(allGround)
            setTmCrew(allCrew)
          } catch {
            setTmShowsIndex([])
            setTmFlights([])
            setTmHotels([])
            setTmGroundTransport([])
            setTmCrew([])
          }
          return
        }

        const singleParams =
          intelScope && intelScope !== 'all' ? { params: { userId: intelScope } } : undefined

        const [shows, songs, financials, tasks, dealsData, cal250, cal50] = await Promise.all([
          getDataAPI('user/getBookings', undefined, singleParams).catch(() => []),
          getDataAPI('user/getCatalog', undefined, singleParams).catch(() => []),
          getDataAPI('financials', undefined, singleParams).catch(() => []),
          getDataAPI('user/tasks?scope=all', undefined, singleParams).catch(() =>
            getDataAPI('user/tasks', undefined, singleParams).catch(() => []),
          ),
          (async () => {
            try {
              return await getDataAPI('deals/revenue-entries', undefined, singleParams)
            } catch {
              try {
                return await getDataAPI('deals', undefined, singleParams)
              } catch {
                return { data: { deals: [] } }
              }
            }
          })(),
          getDataAPI('user/connectors/googleCalendar/events?maxResults=250', undefined, singleParams).catch(() => null),
          getDataAPI('user/connectors/googleCalendar/events?maxResults=50', undefined, singleParams).catch(() => null),
        ])
        const calendarData = cal250 ?? cal50 ?? { data: { data: { events: [] } } }
        const bookingOwnerTag = intelScope && intelScope !== 'all' ? intelScope : userId ?? ''
        const showsTagged = extractArray(shows).map((item: Record<string, unknown>) => ({
          ...item,
          _dashboardArtistId: bookingOwnerTag,
        }))
        setHeroRaw({ shows: showsTagged, songs, tasks, calendar: calendarData })
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getBookings']), showsTagged)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/getCatalog']), songs)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'financials']), financials)
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', 'user/tasks']), tasks)
        const calKey =
          cal250 != null
            ? 'user/connectors/googleCalendar/events?maxResults=250'
            : 'user/connectors/googleCalendar/events?maxResults=50'
        writeSessionCache(makeCacheKey([scopeCacheKey, 'api', calKey]), calendarData)
        applyData(showsTagged, songs, financials, tasks, dealsData, scopeCacheKey)

        try {
          const tmOwner = intelScope && intelScope !== 'all' ? intelScope : userId ?? ''
          const showParamsRecord: Record<string, string> = { limit: '5000' }
          if (tmOwner) showParamsRecord.userId = tmOwner
          const [showRes, tf, th, tg, tc] = await Promise.all([
            getDataAPI('user/tm/shows', undefined, { params: showParamsRecord }).catch(() => []),
            getDataAPI('user/tm/flights', undefined, singleParams).catch(() => []),
            getDataAPI('user/tm/hotels', undefined, singleParams).catch(() => []),
            getDataAPI('user/tm/ground-transport', undefined, singleParams).catch(() => []),
            getDataAPI('user/tm/crew', undefined, singleParams).catch(() => []),
          ])
          const tShows = tmListFromApiPayload(showRes, 'shows') as Record<string, unknown>[]
          setTmShowsIndex(tShows.map((s) => ({ ...s, _dashboardArtistId: tmOwner })))
          setTmFlights(tmTravelListFromApi(tf, 'flights'))
          setTmHotels(tmTravelListFromApi(th, 'hotels'))
          setTmGroundTransport(tmTravelListFromApi(tg, 'ground'))
          setTmCrew(tmTravelListFromApi(tc, 'crew'))
        } catch {
          setTmShowsIndex([])
          setTmFlights([])
          setTmHotels([])
          setTmGroundTransport([])
          setTmCrew([])
        }

        const memberUserId = intelScope && intelScope !== 'all' ? intelScope : effectiveUserId
        if (memberUserId) {
          try {
            const response = await postDataAPI('team/members', { userId: memberUserId, userRole: 'artist' })
            const data = response?.data?.data ?? response?.data ?? response
            const arr = Array.isArray(data) ? data : []
            const map: Record<string, string> = {}
            for (const member of arr) {
              if (member.status !== 'active') continue
              const id = String(member._id ?? member.id)
              map[id] = member.name ?? member.username ?? member.email ?? id
            }
            try {
              const raw = localStorage.getItem('dizeeUser')
              const u = raw ? JSON.parse(raw) : null
              if (u?._id) map[String(u._id)] = u.name ?? u.username ?? map[String(u._id)]
            } catch {
              /* ignore */
            }
            setMemberMap(map)
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err)
        setHeroRaw({
          shows: [],
          songs: [],
          tasks: [],
          calendar: { data: { data: { events: [] } } },
        })
        setTmShowsIndex([])
        setTmFlights([])
        setTmHotels([])
        setTmGroundTransport([])
        setTmCrew([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
    const interval = setInterval(fetchData, 60_000)
    return () => clearInterval(interval)
  }, [userId, artistScopeTick, intelScope, managedArtists, scopeCacheKey, isTeamMember, managedArtistsHydrated])

  const byType = useMemo(() => {
    const m: Record<string, IntelligenceSignal[]> = {}
    for (const s of signals) {
      const key = s.type
      if (!m[key]) m[key] = []
      m[key].push(s)
    }
    return m
  }, [signals])

  const riskSignals = byType['at-risk'] || []
  const pacingSignals = useMemo(() => {
    const list = [...(byType['pacing'] || [])]
    list.sort((a, b) => (a.meta?.daysUntil ?? 999) - (b.meta?.daysUntil ?? 999))
    return list
  }, [byType])
  const streamingSignals = byType['streaming'] || []

  const heroModel = useMemo(() => {
    if (!heroRaw) return null
    return computeDashboardHeroCardModel(heroRaw.shows, heroRaw.songs, heroRaw.tasks)
  }, [heroRaw])

  const showsById = useMemo(() => {
    if (!heroRaw) return new Map<string, ShowData>()
    const shows = normalizeBookings(heroRaw.shows)
    return new Map(shows.map((s) => [String(s.id), s]))
  }, [heroRaw])

  const travelDetailByBookingId = useMemo(() => {
    const out: Record<string, BookingTravelBundle> = {}
    for (const [bid, show] of showsById) {
      const artistId = String(show.dashboardArtistId ?? '')
      const tmForBooking = artistId
        ? tmShowsIndex.filter(
            (s) => String((s as { _dashboardArtistId?: unknown })._dashboardArtistId ?? '') === artistId,
          )
        : tmShowsIndex
      const tid = findTourShowIdForBooking(bid, show, tmForBooking)
      if (!tid) {
        out[bid] = { tourShowId: null, flights: [], hotels: [], ground: [], crew: [] }
        continue
      }
      out[bid] = {
        tourShowId: tid,
        flights: travelItemsForTourShow(tmFlights, tid),
        hotels: travelItemsForTourShow(tmHotels, tid),
        ground: travelItemsForTourShow(tmGroundTransport, tid),
        crew: travelItemsForTourShow(tmCrew, tid),
      }
    }
    return out
  }, [showsById, tmShowsIndex, tmFlights, tmHotels, tmGroundTransport, tmCrew])

  const catalogSongs = useMemo(() => {
    if (!heroRaw) return []
    return normalizeCatalog(heroRaw.songs)
  }, [heroRaw])

  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [assetsModalOpen, setAssetsModalOpen] = useState(false)
  const [linksOpen, setLinksOpen] = useState(false)
  const [linksCopied, setLinksCopied] = useState<string | null>(null)

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  const quickLinks = useMemo(() => {
    const uid = getEffectiveUserId() || ''
    const links: { id: string; label: string; url: string; icon: 'release' | 'ticket' | 'email' }[] = []

    if (uid) {
      const allLinks = loadLinks(uid)
      const releaseLink = allLinks.find((l) => l.linkType === 'release' && l.status === 'active' && l.slug)
      if (releaseLink) {
        links.push({ id: 'release', label: 'Release Link', url: `${baseUrl}/l/release/${releaseLink.slug}`, icon: 'release' })
      }
    }

    return links
  }, [baseUrl, userId])

  const [ticketUrl, setTicketUrl] = useState<string | null>(null)
  const [guestListUrl, setGuestListUrl] = useState<string | null>(null)
  useEffect(() => {
    const ids = getActiveArtistIds()
    const primaryId = ids[0] || getEffectiveUserId() || ''
    if (!primaryId) return
    let cancelled = false

    getDataAPI('tickets/manage/me', undefined, { params: { userId: primaryId } })
      .then((res) => {
        if (cancelled) return
        const d = res as { ticketSlug?: string; data?: { ticketSlug?: string } }
        const slug = d?.data?.ticketSlug ?? d?.ticketSlug ?? null
        if (slug) setTicketUrl(buildTicketPublicUrl(slug))
      })
      .catch(() => {})

    getDataAPI('guestlist/manage/me', undefined, { params: { userId: primaryId } })
      .then((res) => {
        if (cancelled) return
        const d = res as { guestListSlug?: string; data?: { guestListSlug?: string } }
        const slug = d?.data?.guestListSlug ?? d?.guestListSlug ?? null
        if (slug) setGuestListUrl(buildGuestListPublicUrl(slug))
      })
      .catch(() => {})

    return () => { cancelled = true }
  }, [userId])

  const emailAddress = useMemo(() => {
    const uid = getEffectiveUserId() || ''
    if (!uid) return null
    const cached = getArtistFromCache(uid)
    if (cached?.username?.trim()) return dizeeInboxAddress(cached.username.trim())
    const raw = localStorage.getItem('dizeeUser')
    if (raw) {
      try {
        const u = JSON.parse(raw) as { username?: string }
        if (u?.username?.trim()) return dizeeInboxAddress(u.username.trim())
      } catch {}
    }
    return null
  }, [userId])

  const copyToClipboard = (text: string, key: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setLinksCopied(key)
      setTimeout(() => setLinksCopied(null), 2000)
    })
  }

  const [moduleLayout, setModuleLayout] = useState<DashboardModuleLayout>(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_MODULE_LAYOUT_KEY)
      if (raw === 'stacked' || raw === 'sideBySide') return raw
    } catch {
      /* ignore */
    }
    return 'sideBySide'
  })

  const [moduleOrder, setModuleOrder] = useState<DashboardModuleId[]>(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_MODULE_ORDER_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as unknown
        return normalizeDashboardModuleOrder(parsed)
      }
    } catch {
      /* ignore */
    }
    return [...ALL_MODULE_IDS]
  })

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_MODULE_LAYOUT_KEY, moduleLayout)
    } catch {
      /* ignore */
    }
  }, [moduleLayout])

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_MODULE_ORDER_KEY, JSON.stringify(moduleOrder))
    } catch {
      /* ignore */
    }
  }, [moduleOrder])

  const isDesktopModules = useDashboardModuleDesktop()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleModuleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setModuleOrder((items) => {
        const oldIndex = items.indexOf(active.id as DashboardModuleId)
        const newIndex = items.indexOf(over.id as DashboardModuleId)
        if (oldIndex === -1 || newIndex === -1) return items
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const moduleGridClass =
    moduleLayout === 'stacked'
      ? 'grid grid-cols-1 gap-4 min-w-0'
      : 'grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0'

  const moduleSortStrategy =
    moduleLayout === 'stacked' ? verticalListSortingStrategy : rectSortingStrategy

  const skeletonModuleGridClass = isDesktopModules
    ? moduleGridClass
    : 'grid grid-cols-1 gap-4 min-w-0'

  const renderModuleContent = (id: DashboardModuleId) => {
    switch (id) {
      case 'shows':
        return (
          <DashboardShowsCombined
            pacingSignals={pacingSignals}
            riskSignals={riskSignals}
            showsById={showsById}
            hideFinancials={hideFinancialsShows}
            travelDetailByBookingId={travelDetailByBookingId}
          />
        )
      case 'music':
        return <DashboardMusicCombined catalogSongs={catalogSongs} streamingSignals={streamingSignals} />
      case 'tasks':
        return <DashboardTasksWeek tasks={dashboardTasks} memberMap={memberMap} />
      case 'money':
        return (
          <DashboardMoneySection finances={hideFinancialsOverview ? [] : finances} hideFinancials={hideFinancialsOverview} />
        )
      default:
        return null
    }
  }

  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto overflow-x-hidden">
      <UploadDataModal isOpen={uploadModalOpen} onClose={() => setUploadModalOpen(false)} />
      <DashboardAssetsModal isOpen={assetsModalOpen} onClose={() => setAssetsModalOpen(false)} userId={userId} />

      <div className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 pt-16 md:pt-0 pb-4 bg-[rgb(var(--bg-rgb))]">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · refreshes every 60s`
                : 'Loading…'}
            </p>
          </div>
          <div className="flex flex-col w-full md:w-auto md:flex-row md:flex-wrap items-stretch md:items-center gap-2 shrink-0">
            {isTeamMember && !managedArtistsHydrated && (
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto" aria-busy aria-label="Loading artist list">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Artist</span>
                <div className="h-10 min-w-[10rem] max-w-[min(100%,16rem)] rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
              </div>
            )}
            {showArtistFilter && managedArtistsHydrated ? (
              <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
                <label htmlFor="intel-dashboard-artist" className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  Artist
                </label>
                <select
                  id="intel-dashboard-artist"
                  value={artistSelectValue}
                  onChange={(e) => handleIntelArtistFilterChange(e.target.value)}
                  className="ds-select text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 min-w-[10rem] max-w-[min(100%,16rem)] focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400"
                >
                  <option value={ALL_ARTISTS_VALUE}>All artists</option>
                  {managedArtists.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="w-full min-w-0 grid grid-cols-2 gap-2 md:flex md:flex-row md:flex-wrap md:items-center md:gap-2 md:w-auto">
              <Button
                variant="secondary"
                type="button"
                onClick={() => navigate('/digest')}
                onMouseEnter={prefetchDigestPage}
                onFocus={prefetchDigestPage}
                className="w-full min-w-0 justify-center md:w-auto"
              >
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Digest
                </span>
              </Button>
              <Button variant="secondary" onClick={() => setLinksOpen((v) => !v)} className="w-full min-w-0 justify-center md:w-auto">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Links
                  <svg
                    className={`w-3.5 h-3.5 text-gray-400 shrink-0 ${linksOpen ? 'rotate-180' : 'rotate-0'}`}
                    style={{ transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </Button>
              <Button variant="secondary" onClick={() => setAssetsModalOpen(true)} className="w-full min-w-0 justify-center md:w-auto">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Assets
                </span>
              </Button>
              <Button variant="secondary" onClick={() => setUploadModalOpen(true)} className="w-full min-w-0 justify-center md:w-auto">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Data
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div
          className="grid"
          style={{
            gridTemplateRows: linksOpen ? '1fr' : '0fr',
            transition: 'grid-template-rows 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          <div className="overflow-hidden">
            <div className="pt-3 pb-1 space-y-2">
              {quickLinks.map((ql) => (
                <div
                  key={ql.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] px-4 py-3"
                >
                  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">{ql.label}</p>
                    <p className="text-sm text-gray-800 dark:text-zinc-300 truncate">{ql.url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(ql.url, ql.id)}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.1]"
                  >
                    {linksCopied === ql.id ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ))}

              {ticketUrl && (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] px-4 py-3">
                  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Ticket Link</p>
                    <p className="text-sm text-gray-800 dark:text-zinc-300 truncate">{ticketUrl}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(ticketUrl, 'ticket')}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.1]"
                  >
                    {linksCopied === 'ticket' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}

              {guestListUrl && (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] px-4 py-3">
                  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Guest List Link</p>
                    <p className="text-sm text-gray-800 dark:text-zinc-300 truncate">{guestListUrl}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(guestListUrl, 'guestlist')}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.1]"
                  >
                    {linksCopied === 'guestlist' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}

              {emailAddress && (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] px-4 py-3">
                  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">Email</p>
                    <p className="text-sm text-gray-800 dark:text-zinc-300 truncate">{emailAddress}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(emailAddress, 'email')}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.1]"
                  >
                    {linksCopied === 'email' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}

              {quickLinks.length === 0 && !ticketUrl && !guestListUrl && !emailAddress && (
                <p className="text-sm text-gray-500 dark:text-zinc-500 py-2">
                  No links available yet. Create a release link in Links, or set up tickets.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {((isLoading && signals.length === 0) || (isTeamMember && !managedArtistsHydrated)) && (
        <>
          <div className="mb-8 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-6 w-3/4 max-w-md" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700">
              {[0, 1, 2].map((i) => (
                <div key={i} className="p-4 sm:p-6 space-y-2">
                  <Skeleton className="h-2.5 w-20" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              ))}
            </div>
          </div>
          <div className={skeletonModuleGridClass}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 min-h-40 flex flex-col gap-3"
              >
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[85%]" />
                <Skeleton className="h-20 w-full rounded-lg mt-auto" />
              </div>
            ))}
          </div>
        </>
      )}

      {(signals.length > 0 || !isLoading) && managedArtistsHydrated && (
        <>
          {heroModel && (
            <DashboardHeroCard
              greeting={heroModel.greeting}
              todayTasks={heroModel.todayTasks}
              tasksHref={heroModel.tasksHref}
              nextShow={heroModel.nextShow}
              nextRelease={heroModel.nextRelease}
              sectionClassName="mb-8"
            />
          )}

          {isDesktopModules ? (
            <>
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setModuleLayout((m) => (m === 'stacked' ? 'sideBySide' : 'stacked'))}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 dark:border-white/[0.1] dark:bg-[#13120A] dark:text-zinc-300 dark:hover:bg-white/[0.06] dark:focus-visible:ring-gray-400"
                  title={moduleLayout === 'stacked' ? 'Switch to side-by-side columns' : 'Switch to stacked layout'}
                  aria-label={
                    moduleLayout === 'stacked' ? 'Switch to side-by-side columns' : 'Switch to stacked layout'
                  }
                >
                  {moduleLayout === 'stacked' ? (
                    <Columns2 className="h-4 w-4" aria-hidden />
                  ) : (
                    <LayoutList className="h-4 w-4" aria-hidden />
                  )}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-500 sm:text-right">
                  Hover the top-right of a card to drag and rearrange.
                </p>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
                <SortableContext items={moduleOrder} strategy={moduleSortStrategy}>
                  <div className={moduleGridClass}>
                    {moduleOrder.map((id) => (
                      <SortableModuleSection key={id} id={id} title={MODULE_TITLES[id]}>
                        {renderModuleContent(id)}
                      </SortableModuleSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4 min-w-0">
              {moduleOrder.map((id) => (
                <SectionCard key={id} title={MODULE_TITLES[id]}>
                  {renderModuleContent(id)}
                </SectionCard>
              ))}
            </div>
          )}

          <div className="mt-6 mb-4 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Dashboard signals are factual observations from your data — not recommendations.
            </p>
          </div>

          <style>{`
            .intel-scroll {
              scrollbar-gutter: stable;
              scrollbar-width: thin;
              scrollbar-color: rgba(156,163,175,0.55) transparent;
            }
            .intel-scroll::-webkit-scrollbar {
              width: 6px;
            }
            .intel-scroll::-webkit-scrollbar-track {
              background: transparent;
            }
            .intel-scroll::-webkit-scrollbar-thumb {
              background: rgba(156,163,175,0.45);
              border-radius: 4px;
            }
            .intel-scroll::-webkit-scrollbar-thumb:hover {
              background: rgba(156,163,175,0.65);
            }
            .dark .intel-scroll {
              scrollbar-color: rgba(107,114,128,0.6) transparent;
            }
            .dark .intel-scroll::-webkit-scrollbar-thumb {
              background: rgba(107,114,128,0.45);
            }
            .dark .intel-scroll::-webkit-scrollbar-thumb:hover {
              background: rgba(107,114,128,0.65);
            }
          `}</style>
        </>
      )}
    </div>
  )
}
